import { Action } from '@ngrx/store';

import { JobActivityActions } from '../job/job-activity/job-activity.actions';

interface ReplayableChange {
  jobId?: string;
  action: Action;
}

// Requirement: Persist only the latest replayable comment snapshot for each
// job/comment while preserving unrelated actions and distinct workflows.
const SNAPSHOT_ACTION_TYPES = new Set<string>([
  JobActivityActions.addCommentContentsChanged.type,
  JobActivityActions.addCommentSubjectChanged.type,
  JobActivityActions.commentEditContentChanged.type,
  JobActivityActions.replyInputContentChanged.type,
]);

export function getUnsavedChangeSnapshotKey(change: ReplayableChange): string | undefined {
  if (!SNAPSHOT_ACTION_TYPES.has(change.action.type)) {
    return undefined;
  }

  const commentId = 'commentId' in change.action && typeof change.action.commentId === 'string'
    ? change.action.commentId
    : '';

  return [change.jobId || '', change.action.type, commentId].join('|');
}

export function appendUnsavedChange<T extends ReplayableChange>(queue: T[], change: T): T[] {
  const snapshotKey = getUnsavedChangeSnapshotKey(change);
  const retainedChanges = snapshotKey
    ? queue.filter((queuedChange) => getUnsavedChangeSnapshotKey(queuedChange) !== snapshotKey)
    : queue;

  return [...retainedChanges, change];
}

export function compactUnsavedChanges<T extends ReplayableChange>(queue: T[]): T[] {
  const latestIndexBySnapshotKey = new Map<string, number>();

  queue.forEach((change, index) => {
    const snapshotKey = getUnsavedChangeSnapshotKey(change);
    if (snapshotKey) {
      latestIndexBySnapshotKey.set(snapshotKey, index);
    }
  });

  return queue.filter((change, index) => {
    const snapshotKey = getUnsavedChangeSnapshotKey(change);
    return !snapshotKey || latestIndexBySnapshotKey.get(snapshotKey) === index;
  });
}
